import { useState } from 'react';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useCookies } from 'react-cookie';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { useTranslation } from '@hooks';
import CookiePartner from '@components/common/CookieSettings/CookiePartner';
import { ToggleActive, TogglePassive } from '@components/icons';
import { StyledRow } from '@components/styles/CommonStyles';
import { NewButton } from '@components/ui';
import { H3, Subtitle3, Text2 } from '@components/styles/Typography';

const CustomCookies = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const [cookie, setCookie] = useCookies(['user_cookie']);
    const [showPartner, setShowPartner] = useState(false);
    const [firstParty, setFirstParty] = useState(false);
    const [thirdParty, setThirdParty] = useState(false);
    const [advertising, setAdvertising] = useState(false);
    const [cookiePreferences, setCookiePreferences] = useState(null);
    const [partnerPreferences, setPartnerPreferences] = useState({
        cloudflare: false,
        google: false,
        facebook: false,
        pixel: false,
        gAnalytics: false,
        gReMarketing: false,
        gTagManager: false,
    });

    const acceptCookie = () => {
        setCookie('user_cookie', 'accept_all', {
            maxAge: 10000 * 365,
        });

        router.back();
    };

    const acceptSomeCookie = () => {
        setCookiePreferences({
            advertising,
            firstParty,
            thirdParty,
            ...partnerPreferences,
        });
        if (cookiePreferences) {
            const preferences = JSON.stringify(cookiePreferences);

            setCookie('user_cookie', preferences, {
                maxAge: 10000 * 365,
                domain: window.location.hostname.replace('www', ''),
            });
            router.back();
        }
    };

    return (
        <PageWrapper cookie={cookie}>
            <NextSeo
                title={`Fervente | ${t('customCookiesMetaTitle')}`}
                description={t('customCookiesMetaDesc')}
            />
            <ContentWrapper>
                <TitleWrapper>
                    <H3>{t('customizeCookie')}</H3>
                </TitleWrapper>
                <Text2>
                    {t('customCookiePolicyText')}{' '}
                    <Link href="/cookie-policy" to="/cookie-policy" passHref>
                        {t('clickHere')}
                    </Link>
                </Text2>
                <ButtonWrapper>
                    <NewButton
                        size="xlarge"
                        type="secondary"
                        content={t('acceptCookie')}
                        handleClick={acceptCookie}
                        data-testid="accept-cookies"
                    />
                    <NewButton
                        size="xlarge"
                        type="primary"
                        content={t('saveCustomCookie')}
                        handleClick={acceptSomeCookie}
                    />
                </ButtonWrapper>
            </ContentWrapper>
            <ContentWrapper>
                <StyledRow>
                    <TitleWrapper>
                        <H3>{t('advertisingCookies')}</H3>
                    </TitleWrapper>

                    <ToggleButton>
                        <label htmlFor="advertising">
                            {advertising ? <ToggleActive /> : <TogglePassive />}
                        </label>
                        <input
                            id="advertising"
                            type="checkbox"
                            onChange={() => setAdvertising(!advertising)}
                        />
                    </ToggleButton>
                </StyledRow>
                <Text2>{t('advertisingCookieText')}</Text2>
                <CustomizePromotion>
                    <Subtitle3>{t('customizePromotionCookie')}</Subtitle3>
                    <StyledRow>
                        <Text2>{t('ferventeAds')}</Text2>
                        <ToggleButton>
                            <label htmlFor="firstParty">
                                {firstParty ? (
                                    <ToggleActive />
                                ) : (
                                    <TogglePassive />
                                )}
                            </label>
                            <input
                                id="firstParty"
                                type="checkbox"
                                onChange={() => setFirstParty(!firstParty)}
                            />
                        </ToggleButton>
                    </StyledRow>
                    <StyledRow>
                        <Text2>{t('thirdPartyAds')}</Text2>
                        <ToggleButton>
                            <label htmlFor="thirdParty">
                                {thirdParty ? (
                                    <ToggleActive />
                                ) : (
                                    <TogglePassive />
                                )}
                            </label>
                            <input
                                id="thirdParty"
                                type="checkbox"
                                onChange={() => setThirdParty(!thirdParty)}
                            />
                        </ToggleButton>
                    </StyledRow>
                    <NewButton
                        size="xlarge"
                        type="primary"
                        content={t('customizeThirdPartyAds')}
                        handleClick={() => setShowPartner(!showPartner)}
                    />
                </CustomizePromotion>
                {showPartner && (
                    <Partners>
                        <H3>{t('customizeThirdPartyAds')}</H3>
                        <CookiePartner
                            title="facebook pixel"
                            partnerPreferences={partnerPreferences}
                            setPartnerPreferences={setPartnerPreferences}
                        />
                        <CookiePartner
                            title="facebook"
                            partnerPreferences={partnerPreferences}
                            setPartnerPreferences={setPartnerPreferences}
                        />
                        <CookiePartner
                            title="google"
                            partnerPreferences={partnerPreferences}
                            setPartnerPreferences={setPartnerPreferences}
                        />
                        <CookiePartner
                            title="Google Analytics"
                            partnerPreferences={partnerPreferences}
                            setPartnerPreferences={setPartnerPreferences}
                        />
                        <CookiePartner
                            title="Google Tag Manager"
                            partnerPreferences={partnerPreferences}
                            setPartnerPreferences={setPartnerPreferences}
                        />
                        <CookiePartner
                            title="Google Remarketing Tag"
                            partnerPreferences={partnerPreferences}
                            setPartnerPreferences={setPartnerPreferences}
                        />
                    </Partners>
                )}
            </ContentWrapper>
        </PageWrapper>
    );
};
const PageWrapper = styled.div`
    display: ${({ hide }) => hide && 'none'};
    margin: 150px auto;
    max-width: 720px;

    @media (max-width: 768px) {
        margin: 90px 0 0 0;
        padding: 0 20px;
    }
`;
const ButtonWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 20px 0;
    @media (max-width: 1024px) {
        button {
            margin-top: 20px;
        }
    }
`;
const ContentWrapper = styled.div`
    padding: 0 0 20px 0;
    :first-child {
        border-bottom: ${({ theme }) => `1px solid ${theme.colors.bgGray50}`};
    }
    a {
        color: #007185;
    }
`;
const CustomizePromotion = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.colors.tertiaryVariant};
    border: ${({ theme }) => `1px solid ${theme.colors.tertiaryVariant}`};
    padding: 10px;
    margin-top: 20px;
    border-radius: 4px;
    div {
        padding: 10px 0;
    }
    button {
        margin-top: 20px;
        align-self: center;
    }
`;
const ToggleButton = styled.div`
    cursor: pointer;
    input {
        visibility: hidden;
    }
`;
const Partners = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: 20px;
`;
const TitleWrapper = styled.div`
    position: relative;
    padding: 10px 0;
    margin: 14px 0;

    :after {
        position: absolute;
        content: '';
        width: 80px;
        height: 2px;
        background-color: ${({ theme }) => theme.colors.primary};
        bottom: 0;
    }
`;
export default CustomCookies;
